import React, { useMemo } from "react";
import * as THREE from "three";
import { useGLTF } from "@react-three/drei";

import Rombek from "./Rombek";
import RombekWzmocnienie from "./Rombek-wzmocnienie";

/* -------------------- helpers -------------------- */
function toV3(p) {
  if (!p) return new THREE.Vector3();
  if (p.isVector3) return p.clone();
  return new THREE.Vector3(p.x ?? 0, p.y ?? 0, p.z ?? 0);
}

function axisSet(v3, axis, value) {
  if (axis === "x") v3.x = value;
  else if (axis === "y") v3.y = value;
  else v3.z = value;
}

function axisGet(v3, axis) {
  if (axis === "x") return v3.x;
  if (axis === "y") return v3.y;
  return v3.z;
}

function dominantAxesFromSize(size) {
  const arr = [
    { axis: "x", v: size.x },
    { axis: "y", v: size.y },
    { axis: "z", v: size.z },
  ].sort((a, b) => b.v - a.v);

  return {
    lengthAxis: arr[0].axis,
    widthAxis: arr[1].axis,
    thicknessAxis: arr[2].axis,
  };
}

function localAxisVector(axisName) {
  if (axisName === "x") return new THREE.Vector3(1, 0, 0);
  if (axisName === "y") return new THREE.Vector3(0, 1, 0);
  return new THREE.Vector3(0, 0, 1);
}

// build quaternion so that:
// model-length -> lengthDir
// model-width  -> widthDir
// model-thickness -> normal
function buildPanelQuaternion({
                                normal,
                                lengthDir,
                                widthDir,
                                lengthAxis,
                                widthAxis,
                                thicknessAxis,
                                modelCorrectionQ,
                              }) {
  const dirLen = lengthDir.clone().normalize();
  const dirWid = widthDir.clone().normalize();
  const dirThk = normal.clone().normalize();

  const mapAxis = (axisName) => {
    if (axisName === lengthAxis) return dirLen;
    if (axisName === widthAxis) return dirWid;
    return dirThk;
  };

  const worldX = mapAxis("x").clone();
  const worldY = mapAxis("y").clone();
  const worldZ = mapAxis("z").clone();

  const crossXY = new THREE.Vector3().crossVectors(worldX, worldY);
  if (crossXY.dot(worldZ) < 0) worldX.multiplyScalar(-1);

  const q = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(worldX, worldY, worldZ)
  );

  if (modelCorrectionQ) q.multiply(modelCorrectionQ);
  return q;
}

function applyManualPanelRotation(quat, manualPanelRot = [0, 0, 0]) {
  const rx = Number(manualPanelRot?.[0] ?? 0) || 0;
  const ry = Number(manualPanelRot?.[1] ?? 0) || 0;
  const rz = Number(manualPanelRot?.[2] ?? 0) || 0;

  if (rx === 0 && ry === 0 && rz === 0) return quat;

  const qManual = new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz));

  // obrót w osiach świata (łatwiejszy do ręcznego strojenia)
  quat.premultiply(qManual);
  return quat;
}

// ręczny "pochył" po lokalnej osi szerokości połaci (widthDir)
function applyManualPanelPitchAroundFaceWidth(quat, widthDir, rad = 0) {
  const r = Number(rad) || 0;
  if (!r) return quat;

  const axis = widthDir?.clone?.().normalize?.() || new THREE.Vector3(1, 0, 0);
  if (axis.lengthSq() < 1e-10) return quat;

  const q = new THREE.Quaternion().setFromAxisAngle(axis, r);

  // premultiply = obrót w świecie wokół osi połaci
  quat.premultiply(q);
  return quat;
}

function uniquePoints(points, eps = 1e-4) {
  const out = [];
  const epsSq = eps * eps;
  for (const p0 of points || []) {
    const p = toV3(p0);
    let ok = true;
    for (const q of out) {
      if (p.distanceToSquared(q) < epsSq) {
        ok = false;
        break;
      }
    }
    if (ok) out.push(p);
  }
  return out;
}

function uniquePointsFromFace(face) {
  const pts = [];
  if (Array.isArray(face?.points)) {
    for (const p of face.points) pts.push(toV3(p));
  }
  if (Array.isArray(face?.segments)) {
    for (const s of face.segments) {
      if (s?.a) pts.push(toV3(s.a));
      if (s?.b) pts.push(toV3(s.b));
    }
  }
  return uniquePoints(pts, 1e-4);
}

/**
 * Szuka normalnej połaci z punktów (robust dla małej liczby punktów).
 * Bierzemy trójkę o największym polu.
 */
function bestFitRoofFaceNormal(points) {
  const pts = uniquePoints(points, 1e-5);
  if (pts.length < 3) return null;

  let best = null;
  let bestLenSq = 0;

  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      for (let k = j + 1; k < pts.length; k++) {
        const ab = new THREE.Vector3().subVectors(pts[j], pts[i]);
        const ac = new THREE.Vector3().subVectors(pts[k], pts[i]);
        const n = new THREE.Vector3().crossVectors(ab, ac);
        const lenSq = n.lengthSq();
        if (lenSq > bestLenSq) {
          bestLenSq = lenSq;
          best = n;
        }
      }
    }
  }

  if (!best || bestLenSq < 1e-12) return null;
  best.normalize();
  if (best.y < 0) best.multiplyScalar(-1);
  return best;
}

/**
 * roofFaces.points często zawiera punkty z górnej i dolnej płaszczyzny blachy.
 * Rozdzielamy po rzucie na normalną i bierzemy "górną" grupę.
 */
function pickOuterPlanePoints(points, normal) {
  if (!Array.isArray(points) || points.length < 3 || !normal) return points || [];

  const n = normal.clone().normalize();
  const withD = points.map((p0) => {
    const p = toV3(p0);
    return { p, d: p.dot(n) };
  });

  withD.sort((a, b) => a.d - b.d);

  let maxGap = 0;
  let splitIdx = -1;
  for (let i = 0; i < withD.length - 1; i++) {
    const gap = withD[i + 1].d - withD[i].d;
    if (gap > maxGap) {
      maxGap = gap;
      splitIdx = i;
    }
  }

  const dMin = withD[0]?.d ?? 0;
  const dMax = withD[withD.length - 1]?.d ?? 0;
  const dRange = dMax - dMin;

  const fallbackTop = () => {
    const tol = Math.max(0.002, dRange * 0.25);
    const top = withD.filter((x) => x.d >= dMax - tol).map((x) => x.p.clone());
    return top.length >= 3 ? top : withD.map((x) => x.p.clone());
  };

  if (splitIdx < 0 || maxGap < 1e-4) return fallbackTop();

  const upper = withD.slice(splitIdx + 1);
  if (upper.length >= 3) return upper.map((x) => x.p.clone());

  return fallbackTop();
}

/* --------- filtr połaci dla dachu dwuspadowego (1 oś spadu) --------- */
function approxFaceArea(points) {
  const pts = uniquePoints(points, 1e-5);
  if (pts.length < 3) return 0;

  const box = new THREE.Box3().setFromPoints(pts);
  const size = box.getSize(new THREE.Vector3());

  const dims = [size.x, size.y, size.z].sort((a, b) => b - a);
  return Math.max(1e-6, dims[0] * dims[1]);
}

function getRoofFaceMeta(face, idx) {
  const rawPts = uniquePointsFromFace(face);
  if (rawPts.length < 3) return null;

  let normal = bestFitRoofFaceNormal(rawPts);
  if (!normal) return null;

  const topPts = pickOuterPlanePoints(rawPts, normal);
  const topNormal = bestFitRoofFaceNormal(topPts);
  if (topNormal) normal = topNormal;

  const h = new THREE.Vector3(normal.x, 0, normal.z);
  const hLen = h.length();

  return {
    idx,
    normal,
    horizontal: hLen > 1e-8 ? h.multiplyScalar(1 / hLen) : new THREE.Vector3(),
    area: approxFaceArea(topPts),
    isSloped: normal.y > 0.05,
  };
}

/**
 * Dla dachów dwuspadowych (konstrukcja 2 i 6) zostaw tylko połacie
 * należące do jednej osi spadu (X albo Z).
 * Wspiera ręczne override:
 *  - forceGableAxis: "x" | "z"
 *  - forceRoofFaceIndexes: [0, 1]
 */
function filterRoofFacesForSingleSlopeAxis(
  faces,
  {
    activeConstruction,
    debug = false,
    forceGableAxis = null,
    forceRoofFaceIndexes = null,
  } = {}
) {
  if (!Array.isArray(faces) || faces.length === 0) return faces || [];

  if (Array.isArray(forceRoofFaceIndexes) && forceRoofFaceIndexes.length > 0) {
    const idxSet = new Set(
      forceRoofFaceIndexes
        .map((v) => Number(v))
        .filter((v) => Number.isInteger(v) && v >= 0 && v < faces.length)
    );

    const manualFaces = faces.filter((_, i) => idxSet.has(i));

    return manualFaces.length ? manualFaces : faces;
  }

  if (faces.length < 3) return faces || [];
  if (!(activeConstruction === 2 || activeConstruction === 6)) return faces;

  const metas = faces.map((f, i) => getRoofFaceMeta(f, i)).filter(Boolean);
  const sloped = metas.filter((m) => m.isSloped);

  if (sloped.length <= 2) return faces;

  const xScore = sloped.reduce((sum, m) => sum + m.area * Math.abs(m.horizontal.x), 0);
  const zScore = sloped.reduce((sum, m) => sum + m.area * Math.abs(m.horizontal.z), 0);

  const axisFromProp =
    forceGableAxis === "x" || forceGableAxis === "z" ? forceGableAxis : null;

  const dominantAxis = axisFromProp ?? (xScore >= zScore ? "x" : "z");
  const otherAxis = dominantAxis === "x" ? "z" : "x";

  let keep = sloped.filter((m) => {
    const d = Math.abs(m.horizontal[dominantAxis]);
    const o = Math.abs(m.horizontal[otherAxis]);
    return d >= o + 0.15;
  });

  if (keep.length < 2) {
    keep = [...sloped]
      .sort((a, b) => {
        const scoreA =
          (Math.abs(a.horizontal[dominantAxis]) - Math.abs(a.horizontal[otherAxis])) * a.area;
        const scoreB =
          (Math.abs(b.horizontal[dominantAxis]) - Math.abs(b.horizontal[otherAxis])) * b.area;
        return scoreB - scoreA;
      })
      .slice(0, Math.min(2, sloped.length));
  }

  const keepIdx = new Set(keep.map((m) => m.idx));
  const filtered = faces.filter((_, i) => keepIdx.has(i));

  return filtered.length >= 2 ? filtered : faces;
}

/* --------- hard lock przechyłu do jednej osi (X albo Z) --------- */
function lockBasisToSingleSlopeAxis({
                                      normal,
                                      widthDir,
                                      lengthDir,
                                      upHint,
                                      forceGableAxis,
                                    }) {
  if (forceGableAxis !== "x" && forceGableAxis !== "z") {
    return { normal, widthDir, lengthDir };
  }

  const acrossAxis = forceGableAxis === "x" ? "z" : "x";
  const acrossUnit =
    acrossAxis === "x"
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 0, 1);

  let w = acrossUnit.clone();
  if (widthDir && widthDir.dot(w) < 0) w.multiplyScalar(-1);

  let l = lengthDir.clone();
  if (forceGableAxis === "x") l.z = 0;
  else l.x = 0;

  if (l.lengthSq() < 1e-10) {
    const h = (upHint?.clone() || new THREE.Vector3(0, 1, 0));
    if (forceGableAxis === "x") h.z = 0;
    else h.x = 0;

    if (h.lengthSq() > 1e-10) {
      l.copy(h);
    } else {
      l.copy(
        forceGableAxis === "x"
          ? new THREE.Vector3(1, 0.001, 0)
          : new THREE.Vector3(0, 0.001, 1)
      );
    }
  }

  l.normalize();
  if (upHint && l.dot(upHint) < 0) l.multiplyScalar(-1);

  let n = new THREE.Vector3().crossVectors(w, l).normalize();
  if (n.y < 0) n.multiplyScalar(-1);

  l = new THREE.Vector3().crossVectors(n, w).normalize();
  if (upHint && l.dot(upHint) < 0) l.multiplyScalar(-1);

  if (forceGableAxis === "x") l.z = 0;
  else l.x = 0;
  if (l.lengthSq() < 1e-10) {
    l.copy(forceGableAxis === "x" ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1));
  }
  l.normalize();

  n = new THREE.Vector3().crossVectors(w, l).normalize();
  if (n.y < 0) n.multiplyScalar(-1);

  return {
    normal: n,
    widthDir: w,
    lengthDir: l,
  };
}

/* -------------------- debug visuals -------------------- */
function DebugPoint({ position, size = 0.06 }) {
  return (
    <mesh position={position} frustumCulled={false} renderOrder={9999}>
      <sphereGeometry args={[size, 10, 10]} />
      <meshBasicMaterial depthTest={false} />
    </mesh>
  );
}

function DebugEdge({ a, b }) {
  const geom = useMemo(
    () => new THREE.BufferGeometry().setFromPoints([a, b]),
    [a, b]
  );
  return (
    <line frustumCulled={false}>
      <primitive object={geom} attach="geometry" />
      <lineBasicMaterial attach="material" depthTest={false} />
    </line>
  );
}

function DebugArrow({ from, to }) {
  const geom = useMemo(
    () => new THREE.BufferGeometry().setFromPoints([from, to]),
    [from, to]
  );
  return (
    <line frustumCulled={false}>
      <primitive object={geom} attach="geometry" />
      <lineBasicMaterial attach="material" depthTest={false} />
    </line>
  );
}

/* -------------------- face solver (roofFaces mode) -------------------- */
function solvePanelsOnSingleRoofFace({
                                       face,
                                       faceIdx = 0,

                                       modelBounds,
                                       lengthAxis,
                                       widthAxis,
                                       thicknessAxis,
                                       modelCorrectionQ,

                                       effectiveSurfaceLift,
                                       pitchDeltaRad,

                                       reinforcementEvery,
                                       panelWidthMultiplier,
                                       maxPanels,
                                       minPanels,

                                       debug,
                                       forceGableAxis = null,

                                       // globalny manualny obrót [x,y,z] (opcjonalny)
                                       manualPanelRot = [0, 0, 0],

                                       // manualny pitch po lokalnej osi widthDir
                                       manualPanelPitchAroundWidthRad = 0,
                                     }) {
  const rawFacePoints = uniquePointsFromFace(face);
  const faceSegs = Array.isArray(face?.segments)
    ? face.segments
      .filter((s) => s?.a && s?.b)
      .map((s) => ({ a: toV3(s.a), b: toV3(s.b) }))
    : [];

  if (rawFacePoints.length < 3) {
    console.warn("[DachRombek] face has too few points:", faceIdx, rawFacePoints.length);
    return {
      allSegs: faceSegs,
      lowestSegs: [],
      highestSegs: [],
      panels: [],
      arrows: [],
      panelCount: 0,
    };
  }

  let normal = bestFitRoofFaceNormal(rawFacePoints);
  if (!normal) {
    console.warn("[DachRombek] cannot build face normal:", faceIdx);
    return {
      allSegs: faceSegs,
      lowestSegs: [],
      highestSegs: [],
      panels: [],
      arrows: [],
      panelCount: 0,
    };
  }

  const facePoints = pickOuterPlanePoints(rawFacePoints, normal);

  const topNormal = bestFitRoofFaceNormal(facePoints);
  if (topNormal) normal = topNormal;

  const box = new THREE.Box3().setFromPoints(facePoints);
  const center = box.getCenter(new THREE.Vector3());

  let highestPoint = facePoints[0].clone();
  let lowestPoint = facePoints[0].clone();
  for (const p of facePoints) {
    if (p.y > highestPoint.y) highestPoint = p.clone();
    if (p.y < lowestPoint.y) lowestPoint = p.clone();
  }

  const segsForBands = faceSegs.length
    ? faceSegs
    : (() => {
      const tmp = [];
      for (let i = 0; i < facePoints.length; i++) {
        for (let j = i + 1; j < facePoints.length; j++) {
          if (facePoints[i].distanceTo(facePoints[j]) > 1e-3) {
            tmp.push({ a: facePoints[i].clone(), b: facePoints[j].clone() });
          }
        }
      }
      return tmp;
    })();

  const segMeta = segsForBands.map((s) => {
    const avgY = (s.a.y + s.b.y) * 0.5;
    const len = s.a.distanceTo(s.b);
    const d = new THREE.Vector3().subVectors(s.b, s.a);
    const dXZ = d.clone();
    dXZ.y = 0;
    return {
      seg: s,
      avgY,
      len,
      dXZ,
      absDY: Math.abs(s.b.y - s.a.y),
    };
  });

  let minSegY = Infinity;
  let maxSegY = -Infinity;
  for (const m of segMeta) {
    minSegY = Math.min(minSegY, m.avgY);
    maxSegY = Math.max(maxSegY, m.avgY);
  }
  const yRange = Math.max(1e-6, maxSegY - minSegY);
  const bandTol = Math.max(0.02, yRange * 0.18);

  const lowestSegs = segMeta
    .filter((m) => m.avgY <= minSegY + bandTol)
    .sort((a, b) => b.len - a.len)
    .map((m) => m.seg);

  const highestSegs = segMeta
    .filter((m) => m.avgY >= maxSegY - bandTol)
    .sort((a, b) => b.len - a.len)
    .map((m) => m.seg);

  let widthDir = new THREE.Vector3();

  const accLow = new THREE.Vector3();
  for (const s of lowestSegs) {
    const d = new THREE.Vector3().subVectors(s.b, s.a);
    d.y = 0;
    if (d.lengthSq() > 1e-10) accLow.add(d.normalize());
  }

  if (accLow.lengthSq() > 1e-10) {
    widthDir.copy(accLow.normalize());
  } else if (highestSegs.length) {
    const accHigh = new THREE.Vector3();
    for (const s of highestSegs) {
      const d = new THREE.Vector3().subVectors(s.b, s.a);
      d.y = 0;
      if (d.lengthSq() > 1e-10) accHigh.add(d.normalize());
    }
    if (accHigh.lengthSq() > 1e-10) widthDir.copy(accHigh.normalize());
  }

  if (widthDir.lengthSq() < 1e-10) {
    const mean = center.clone();
    let best = new THREE.Vector3(1, 0, 0);
    let bestVar = -Infinity;
    const dirs = [
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(1, 0, 1).normalize(),
      new THREE.Vector3(1, 0, -1).normalize(),
    ];
    for (const d of dirs) {
      let sum = 0;
      for (const p of facePoints) {
        const rel = p.clone().sub(mean);
        rel.y = 0;
        const t = rel.dot(d);
        sum += t * t;
      }
      if (sum > bestVar) {
        bestVar = sum;
        best = d.clone();
      }
    }
    widthDir.copy(best.normalize());
  }

  let lengthDir = new THREE.Vector3().crossVectors(widthDir, normal).normalize();

  const upHint = new THREE.Vector3().subVectors(highestPoint, lowestPoint);
  if (lengthDir.dot(upHint) < 0) lengthDir.multiplyScalar(-1);

  if (pitchDeltaRad !== 0) {
    lengthDir.applyAxisAngle(widthDir, pitchDeltaRad).normalize();
    lengthDir.sub(normal.clone().multiplyScalar(lengthDir.dot(normal))).normalize();
    if (lengthDir.dot(upHint) < 0) lengthDir.multiplyScalar(-1);
  }

  if (forceGableAxis === "x" || forceGableAxis === "z") {
    const locked = lockBasisToSingleSlopeAxis({
      normal,
      widthDir,
      lengthDir,
      upHint,
      forceGableAxis,
    });

    normal = locked.normal;
    widthDir = locked.widthDir;
    lengthDir = locked.lengthDir;
  } else {
    widthDir = new THREE.Vector3().crossVectors(normal, lengthDir).normalize();
    if (widthDir.lengthSq() < 1e-10) widthDir = new THREE.Vector3(1, 0, 0);
  }

  const origin = lowestPoint.clone();

  let lMin = Infinity;
  let lMax = -Infinity;
  let tMin = Infinity;
  let tMax = -Infinity;

  for (const p of facePoints) {
    const rel = p.clone().sub(origin);
    const l = rel.dot(lengthDir);
    const t = rel.dot(widthDir);
    lMin = Math.min(lMin, l);
    lMax = Math.max(lMax, l);
    tMin = Math.min(tMin, t);
    tMax = Math.max(tMax, t);
  }

  const lengthSpan = Math.max(1e-6, lMax - lMin);
  const stepSpan = Math.max(1e-6, tMax - tMin);

  const widthMinLocal = axisGet(modelBounds.min, widthAxis);
  const widthMaxLocal = axisGet(modelBounds.max, widthAxis);
  const modelWidthSpan = Math.max(1e-6, widthMaxLocal - widthMinLocal);

  const lenMinLocal = axisGet(modelBounds.min, lengthAxis);
  const lenMaxLocal = axisGet(modelBounds.max, lengthAxis);
  const modelLenSpan = Math.max(1e-6, lenMaxLocal - lenMinLocal);

  const idealCount = stepSpan / modelWidthSpan;
  let scaleWidth = 1;

  if (idealCount > maxPanels) scaleWidth = idealCount / maxPanels;
  else if (idealCount < minPanels) scaleWidth = Math.max(0.25, idealCount / minPanels);

  const widthMult = Math.max(0.1, Number(panelWidthMultiplier) || 1);
  scaleWidth *= widthMult;

  const desiredPitch = modelWidthSpan * scaleWidth;
  let count = Math.round(stepSpan / desiredPitch);
  count = Math.max(1, Math.min(maxPanels, Math.max(minPanels, count)));

  const panelPitch = stepSpan / count;
  scaleWidth = panelPitch / modelWidthSpan;

  const lenScale = lengthSpan / modelLenSpan;

  const startT = tMin - widthMinLocal * scaleWidth;
  const startL = lMin - lenMinLocal * lenScale;

  let quat = buildPanelQuaternion({
    normal,
    lengthDir,
    widthDir,
    lengthAxis,
    widthAxis,
    thicknessAxis,
    modelCorrectionQ,
  });

  {
    const localLen = localAxisVector(lengthAxis);
    const worldLen = localLen.clone().applyQuaternion(quat).normalize();

    const wl = worldLen.clone();
    wl.y = 0;
    const wd = widthDir.clone();
    wd.y = 0;

    const slopeXZ = lengthDir.clone();
    slopeXZ.y = 0;

    const slopeScore =
      wl.lengthSq() > 1e-12 && slopeXZ.lengthSq() > 1e-12
        ? Math.abs(wl.normalize().dot(slopeXZ.normalize()))
        : 0;
    const acrossScore =
      wl.lengthSq() > 1e-12 && wd.lengthSq() > 1e-12
        ? Math.abs(wl.normalize().dot(wd.normalize()))
        : 0;

    if (slopeScore < acrossScore) {
      const q90 = new THREE.Quaternion().setFromAxisAngle(normal, Math.PI / 2);
      quat = q90.multiply(quat);
    }
  }

  // najważniejsza korekta: po osi lokalnej szerokości połaci
  quat = applyManualPanelPitchAroundFaceWidth(
    quat,
    widthDir,
    manualPanelPitchAroundWidthRad
  );

  // opcjonalna korekta globalna X/Y/Z
  quat = applyManualPanelRotation(quat, manualPanelRot);

  const panels = [];
  for (let i = 0; i < count; i++) {
    const t = startT + i * panelPitch;
    const l = startL;

    const scale = new THREE.Vector3(1, 1, 1);
    axisSet(scale, lengthAxis, lenScale);
    axisSet(scale, widthAxis, scaleWidth);
    axisSet(scale, thicknessAxis, 1);

    const pos = origin
      .clone()
      .addScaledVector(lengthDir, l)
      .addScaledVector(widthDir, t)
      .addScaledVector(normal, effectiveSurfaceLift);

    panels.push({
      key: `face-${faceIdx}-panel-${i}`,
      position: pos,
      quaternion: quat.clone(),
      scale,
      reinforced: reinforcementEvery > 0 ? i % reinforcementEvery === 0 : false,
    });
  }

  const arrows = [];
  if (debug) {
    const c = center.clone().addScaledVector(normal, effectiveSurfaceLift * 2);
    arrows.push({
      key: `face-${faceIdx}-widthDir`,
      from: c,
      to: c.clone().addScaledVector(widthDir, 1.5),
    });
    arrows.push({
      key: `face-${faceIdx}-lengthDir`,
      from: c,
      to: c.clone().addScaledVector(lengthDir, 1.5),
    });
    arrows.push({
      key: `face-${faceIdx}-normal`,
      from: c,
      to: c.clone().addScaledVector(normal, 1.0),
    });
  }

  return {
    allSegs: faceSegs,
    lowestSegs,
    highestSegs,
    panels,
    arrows,
    panelCount: panels.length,
  };
}

/* -------------------- legacy solver (roofEdges mode) -------------------- */
function solvePanelsFromEdgesLegacy({
                                      edges,
                                      modelBounds,
                                      lengthAxis,
                                      widthAxis,
                                      thicknessAxis,
                                      modelCorrectionQ,
                                      effectiveSurfaceLift,
                                      pitchDeltaRad,
                                      reinforcementEvery,
                                      panelWidthMultiplier,
                                      maxPanels,
                                      minPanels,
                                      flatSideEps,
                                      lowestEps,
                                      debug,

                                      // globalny manualny obrót [x,y,z]
                                      manualPanelRot = [0, 0, 0],

                                      // manualny pitch po lokalnej osi połaci
                                      manualPanelPitchAroundWidthRad = 0,
                                    }) {
  const segs = [];
  const pts = [];

  for (const e of edges) {
    if (!e?.a || !e?.b) continue;
    const a = toV3(e.a);
    const b = toV3(e.b);
    segs.push({ a, b });
    pts.push(a, b);
  }

  if (!segs.length) {
    return { allSegs: [], lowestSegs: [], highestSegs: [], panels: [], arrows: [] };
  }

  const uniq = uniquePoints(pts, 1e-4);

  const box = new THREE.Box3().setFromPoints(uniq);
  const center = box.getCenter(new THREE.Vector3());

  let highestPoint = uniq[0]?.clone() ?? new THREE.Vector3();
  let lowestPoint = uniq[0]?.clone() ?? new THREE.Vector3();
  for (const p of uniq) {
    if (p.y > highestPoint.y) highestPoint = p.clone();
    if (p.y < lowestPoint.y) lowestPoint = p.clone();
  }

  const minX = box.min.x;
  const maxX = box.max.x;
  const minZ = box.min.z;
  const maxZ = box.max.z;

  const xSpan = Math.max(1e-6, maxX - minX);
  const zSpan = Math.max(1e-6, maxZ - minZ);
  const sideSnap = Math.max(0.03, Math.min(xSpan, zSpan) * 0.06);

  const segMeta = segs.map((seg) => {
    const ax = seg.a.x;
    const bx = seg.b.x;
    const az = seg.a.z;
    const bz = seg.b.z;

    const scoreMinX = Math.max(Math.abs(ax - minX), Math.abs(bx - minX));
    const scoreMaxX = Math.max(Math.abs(ax - maxX), Math.abs(bx - maxX));
    const scoreMinZ = Math.max(Math.abs(az - minZ), Math.abs(bz - minZ));
    const scoreMaxZ = Math.max(Math.abs(az - maxZ), Math.abs(bz - maxZ));

    let side = "minX";
    let best = scoreMinX;
    if (scoreMaxX < best) {
      best = scoreMaxX;
      side = "maxX";
    }
    if (scoreMinZ < best) {
      best = scoreMinZ;
      side = "minZ";
    }
    if (scoreMaxZ < best) {
      best = scoreMaxZ;
      side = "maxZ";
    }

    const nearAny = best <= sideSnap;
    const avgY = (seg.a.y + seg.b.y) * 0.5;

    return { seg, side, nearAny, avgY };
  });

  const sides = { minX: [], maxX: [], minZ: [], maxZ: [] };
  for (const m of segMeta) if (m.nearAny) sides[m.side].push(m);

  const sideStats = Object.entries(sides).map(([side, arr]) => {
    if (!arr.length) {
      return {
        side,
        count: 0,
        meanY: Infinity,
        yRange: Infinity,
        isFlat: false,
        arr,
      };
    }

    let sum = 0;
    let minY2 = Infinity;
    let maxY2 = -Infinity;
    for (const m of arr) {
      sum += m.avgY;
      minY2 = Math.min(minY2, m.seg.a.y, m.seg.b.y);
      maxY2 = Math.max(maxY2, m.seg.a.y, m.seg.b.y);
    }
    const meanY = sum / arr.length;
    const yRange = maxY2 - minY2;
    const isFlat = yRange <= flatSideEps;
    return { side, count: arr.length, meanY, yRange, isFlat, arr };
  });

  const flatSides = sideStats.filter((s) => s.count > 0 && s.isFlat);

  let lowestSide = null;
  let highestSide = null;

  if (flatSides.length) {
    let minMeanY = Infinity;
    let maxMeanY = -Infinity;
    for (const s of flatSides) {
      minMeanY = Math.min(minMeanY, s.meanY);
      maxMeanY = Math.max(maxMeanY, s.meanY);
    }

    const lowCandidates = flatSides.filter((s) => s.meanY <= minMeanY + lowestEps);
    lowCandidates.sort((a, b) => b.count - a.count);
    lowestSide = lowCandidates[0]?.side ?? null;

    const highCandidates = flatSides.filter((s) => s.meanY >= maxMeanY - lowestEps);
    highCandidates.sort((a, b) => b.count - a.count);
    highestSide = highCandidates[0]?.side ?? null;
  }

  let lowestSegsLocal = [];
  let highestSegsLocal = [];

  if (lowestSide) lowestSegsLocal = sides[lowestSide].map((m) => m.seg);
  if (highestSide) highestSegsLocal = sides[highestSide].map((m) => m.seg);

  if (!lowestSegsLocal.length) {
    const sorted = [...segMeta].sort((a, b) => a.avgY - b.avgY);
    lowestSegsLocal = sorted.slice(0, Math.min(4, sorted.length)).map((m) => m.seg);
  }
  if (!highestSegsLocal.length) {
    const sorted = [...segMeta].sort((a, b) => b.avgY - a.avgY);
    highestSegsLocal = sorted.slice(0, Math.min(4, sorted.length)).map((m) => m.seg);
  }

  const acc = new THREE.Vector3();
  for (const s of lowestSegsLocal) {
    const d = new THREE.Vector3().subVectors(s.b, s.a);
    d.y = 0;
    if (d.lengthSq() > 1e-10) acc.add(d.normalize());
  }
  const acrossXZ = acc.lengthSq() > 1e-10 ? acc.normalize() : new THREE.Vector3(1, 0, 0);
  const widthDir = acrossXZ.clone().normalize();

  let slopeXZ = new THREE.Vector3(-acrossXZ.z, 0, acrossXZ.x).normalize();

  {
    const downXZ = new THREE.Vector3().subVectors(lowestPoint, highestPoint);
    downXZ.y = 0;
    if (downXZ.lengthSq() > 1e-10) {
      downXZ.normalize();
      if (slopeXZ.dot(downXZ) < 0) slopeXZ.multiplyScalar(-1);
    }
  }

  const origin = lowestPoint.clone();

  let sumL = 0;
  let sumY = 0;
  let sumLL = 0;
  let sumLY = 0;
  const n = Math.max(1, uniq.length);

  for (const p of uniq) {
    const rel = p.clone().sub(origin);
    const L = rel.dot(slopeXZ);
    const Y = rel.y;
    sumL += L;
    sumY += Y;
    sumLL += L * L;
    sumLY += L * Y;
  }

  const meanL = sumL / n;
  const meanY = sumY / n;
  const denom = sumLL - n * meanL * meanL;
  const numer = sumLY - n * meanL * meanY;
  const a = Math.abs(denom) > 1e-9 ? numer / denom : 0;

  let slopeSurface = slopeXZ.clone().add(new THREE.Vector3(0, a, 0)).normalize();

  {
    const down3D = new THREE.Vector3().subVectors(lowestPoint, highestPoint);
    if (slopeSurface.dot(down3D) < 0) slopeSurface.multiplyScalar(-1);
  }

  slopeSurface
    .sub(widthDir.clone().multiplyScalar(slopeSurface.dot(widthDir)))
    .normalize();

  if (pitchDeltaRad !== 0) {
    slopeSurface.applyAxisAngle(widthDir, pitchDeltaRad).normalize();
    slopeSurface
      .sub(widthDir.clone().multiplyScalar(slopeSurface.dot(widthDir)))
      .normalize();
  }

  const lengthDir = slopeSurface.clone().normalize();

  let normal = new THREE.Vector3().crossVectors(widthDir, lengthDir).normalize();
  if (normal.y < 0) normal.multiplyScalar(-1);

  let lMin = Infinity;
  let lMax = -Infinity;
  let tMin = Infinity;
  let tMax = -Infinity;

  for (const p of uniq) {
    const rel = p.clone().sub(origin);
    const l = rel.dot(lengthDir);
    const t = rel.dot(widthDir);
    lMin = Math.min(lMin, l);
    lMax = Math.max(lMax, l);
    tMin = Math.min(tMin, t);
    tMax = Math.max(tMax, t);
  }

  const lengthSpan = Math.max(1e-6, lMax - lMin);
  const stepSpan = Math.max(1e-6, tMax - tMin);

  const widthMinLocal = axisGet(modelBounds.min, widthAxis);
  const widthMaxLocal = axisGet(modelBounds.max, widthAxis);
  const modelWidthSpan = Math.max(1e-6, widthMaxLocal - widthMinLocal);

  const lenMinLocal = axisGet(modelBounds.min, lengthAxis);
  const lenMaxLocal = axisGet(modelBounds.max, lengthAxis);
  const modelLenSpan = Math.max(1e-6, lenMaxLocal - lenMinLocal);

  const idealCount = stepSpan / modelWidthSpan;
  let scaleWidth = 1;

  if (idealCount > maxPanels) scaleWidth = idealCount / maxPanels;
  else if (idealCount < minPanels) scaleWidth = Math.max(0.25, idealCount / minPanels);

  const widthMult = Math.max(0.1, Number(panelWidthMultiplier) || 1);
  scaleWidth *= widthMult;

  const desiredPitch = modelWidthSpan * scaleWidth;
  let count = Math.round(stepSpan / desiredPitch);
  count = Math.max(1, Math.min(maxPanels, Math.max(minPanels, count)));

  const panelPitch = stepSpan / count;
  scaleWidth = panelPitch / modelWidthSpan;

  const lenScale = lengthSpan / modelLenSpan;

  const startT = tMin - widthMinLocal * scaleWidth;
  const startL = lMin - lenMinLocal * lenScale;

  let quat = buildPanelQuaternion({
    normal,
    lengthDir,
    widthDir,
    lengthAxis,
    widthAxis,
    thicknessAxis,
    modelCorrectionQ,
  });

  {
    const localLen = localAxisVector(lengthAxis);
    const worldLen = localLen.clone().applyQuaternion(quat).normalize();

    const ws = worldLen.clone();
    ws.y = 0;
    const ss = slopeXZ.clone();

    const slopeScore = ws.lengthSq() > 1e-12 ? Math.abs(ws.normalize().dot(ss)) : 0;
    const acrossScore =
      ws.lengthSq() > 1e-12
        ? Math.abs(ws.normalize().dot(widthDir.clone().normalize()))
        : 0;

    if (slopeScore < acrossScore) {
      const q90 = new THREE.Quaternion().setFromAxisAngle(normal, Math.PI / 2);
      quat = q90.multiply(quat);
    }
  }

  // korekta po osi połaci
  quat = applyManualPanelPitchAroundFaceWidth(
    quat,
    widthDir,
    manualPanelPitchAroundWidthRad
  );

  // opcjonalna korekta globalna
  quat = applyManualPanelRotation(quat, manualPanelRot);

  const outPanels = [];
  for (let i = 0; i < count; i++) {
    const t = startT + i * panelPitch;
    const l = startL;

    const scale = new THREE.Vector3(1, 1, 1);
    axisSet(scale, lengthAxis, lenScale);
    axisSet(scale, widthAxis, scaleWidth);
    axisSet(scale, thicknessAxis, 1);

    const pos = origin
      .clone()
      .addScaledVector(lengthDir, l)
      .addScaledVector(widthDir, t)
      .addScaledVector(normal, effectiveSurfaceLift);

    outPanels.push({
      key: `panel-${i}`,
      position: pos,
      quaternion: quat.clone(),
      scale,
      reinforced: reinforcementEvery > 0 ? i % reinforcementEvery === 0 : false,
    });
  }

  const arr = [];
  if (debug) {
    const c = center.clone().addScaledVector(normal, effectiveSurfaceLift * 2);
    arr.push({ key: "widthDir", from: c, to: c.clone().addScaledVector(widthDir, 2) });
    arr.push({ key: "lengthDir", from: c, to: c.clone().addScaledVector(lengthDir, 2) });
    arr.push({ key: "normal", from: c, to: c.clone().addScaledVector(normal, 2) });
  }

  return {
    allSegs: segs,
    lowestSegs: lowestSegsLocal,
    highestSegs: highestSegsLocal,
    panels: outPanels,
    arrows: arr,
  };
}

/* -------------------- component -------------------- */
export default function DachRombek({
                                     toolingData,
                                     modelUrl = "/models/rombek/rombek.glb",

                                     surfaceLift = 0.12,
                                     roofType: roofTypeProp,
                                     activeConstruction: activeConstructionProp,

                                     // Obsługuje m.in.:
                                     // - pitchDeltaRad
                                     // - liftDelta / liftOverride
                                     // - forceGableAxis
                                     // - forceRoofFaceIndexes
                                     // - manualPanelRot: [x, y, z]
                                     // - manualPanelPitchAroundWidthRad (global fallback)
                                     // - manualPanelPitchAroundWidthRadByFace: [face0, face1, ...]
                                     roofTuning: roofTuningProp,

                                     reinforcementEvery = 0,
                                     panelWidthMultiplier = 9.0,

                                     maxPanels = 220,
                                     minPanels = 2,

                                     flatSideEps = 0.03,
                                     lowestEps = 0.03,

                                     pivotAtStart = true,
                                     modelCorrectionEuler = [0, 0, 0],

                                     debug = true,
                                     secondHeight = null,
                                     colors = null,
                                   }) {
  const edges = toolingData?.roofEdges || [];
  const roofFacesInput = Array.isArray(toolingData?.roofFaces) ? toolingData.roofFaces : [];

  const gltf = useGLTF(modelUrl);

  const roofType = roofTypeProp ?? toolingData?.roofType ?? toolingData?.roofKind ?? "default";
  const activeConstruction = Number(
    activeConstructionProp ?? toolingData?.activeConstruction ?? toolingData?.activeConstructionType ?? 0
  );

  const roofTuning = useMemo(() => {
    if (roofTuningProp) return roofTuningProp;

    let additionalLift = 0;

    switch (roofType) {
      case 1:

        if (secondHeight > 250) additionalLift = 0.02;

        return {
          pitchDeltaRad: 0.009,
          liftDelta: -0.11 + additionalLift,
          manualPanelRot: [0, 0, 0],
          manualPanelPitchAroundWidthRad: 0,
          manualPanelPitchAroundWidthRadByFace: null,
        };

      case 2:
        return {
          pitchDeltaRad: 0.01,
          liftDelta: -0.11,
          manualPanelRot: [0, 0, 0],
          manualPanelPitchAroundWidthRad: 0,
          manualPanelPitchAroundWidthRadByFace: null,

          // opcjonalnie:
          // forceGableAxis: "x",
          // forceRoofFaceIndexes: [0, 1],
        };

      case 3:
        if (secondHeight > 250) additionalLift = 0.02;

        return {
          pitchDeltaRad: 0.009,
          liftDelta: -0.11 + additionalLift,
          manualPanelRot: [0, 0, 0],
          manualPanelPitchAroundWidthRad: 0,
          manualPanelPitchAroundWidthRadByFace: null,
        };

      case 4:
        if (secondHeight > 250) additionalLift = 0.02;

        return {
          pitchDeltaRad: 0.009,
          liftDelta: -0.11 + additionalLift,
          manualPanelRot: [0, 0, 0],
          manualPanelPitchAroundWidthRad: 0,
          manualPanelPitchAroundWidthRadByFace: null,
        };

      case 5:
        if (secondHeight > 250) additionalLift = 0.02;

        return {
          pitchDeltaRad: 0.009,
          liftDelta: -0.11 + additionalLift,
          manualPanelRot: [0, 0, 0],
          manualPanelPitchAroundWidthRad: 0,
          manualPanelPitchAroundWidthRadByFace: null,
        };

      case 6:
        return {
          pitchDeltaRad: 0.01,
          liftDelta: -0.11,

          // opcjonalnie (zostaw wyłączone jeśli nie chcesz):
          // forceGableAxis: "x",
          // forceRoofFaceIndexes: [0, 1],

          // global fallback (gdy nie ustawisz ByFace)
          manualPanelPitchAroundWidthRad: 0.0,

          // ✅ najważniejsze: osobno dla połaci (po indeksie faceIdx)
          // przykład: popraw tylko drugą połać
          // [pierwsza, druga]
          manualPanelPitchAroundWidthRadByFace: [0, 0],

          // opcjonalna korekta globalna (zwykle zostaw 0)
          manualPanelRot: [0, 0, 0],
        };

      default:
        return {
          pitchDeltaRad: 0.0,
          liftDelta: 0.0,
          manualPanelRot: [0, 0, 0],
          manualPanelPitchAroundWidthRad: 0,
          manualPanelPitchAroundWidthRadByFace: null,
        };
    }
  }, [roofType, roofTuningProp]);

  const pitchDeltaRad = Number(roofTuning?.pitchDeltaRad ?? 0) || 0;

  const effectiveSurfaceLift =
    roofTuning?.liftOverride != null
      ? Number(roofTuning.liftOverride)
      : surfaceLift + (Number(roofTuning?.liftDelta ?? 0) || 0);

  const forceGableAxis = roofTuning?.forceGableAxis ?? null;
  const forceRoofFaceIndexes = roofTuning?.forceRoofFaceIndexes ?? null;
  const manualPanelRot = roofTuning?.manualPanelRot ?? [0, 0, 0];

  // global fallback
  const manualPanelPitchAroundWidthRad =
    Number(roofTuning?.manualPanelPitchAroundWidthRad ?? 0) || 0;

  // osobno dla połaci
  const manualPanelPitchAroundWidthRadByFace =
    roofTuning?.manualPanelPitchAroundWidthRadByFace ?? null;

  const roofFaces = useMemo(
    () =>
      filterRoofFacesForSingleSlopeAxis(roofFacesInput, {
        activeConstruction,
        debug,
        forceGableAxis,
        forceRoofFaceIndexes,
      }),
    [roofFacesInput, activeConstruction, debug, forceGableAxis, forceRoofFaceIndexes]
  );

  const modelBounds = useMemo(() => {
    const nodes = gltf?.nodes ?? {};
    const anyNode = Object.values(nodes).find((n) => n?.geometry);
    const geom = anyNode?.geometry;
    if (!geom) {
      return new THREE.Box3(
        new THREE.Vector3(-0.5, -0.5, -0.5),
        new THREE.Vector3(0.5, 0.5, 0.5)
      );
    }
    if (!geom.boundingBox) geom.computeBoundingBox();
    return geom.boundingBox.clone();
  }, [gltf]);

  const modelSize = useMemo(() => {
    const nodes = gltf?.nodes ?? {};
    const anyNode = Object.values(nodes).find((n) => n?.geometry);
    const geom = anyNode?.geometry;
    if (!geom) return new THREE.Vector3(1, 1, 1);
    if (!geom.boundingBox) geom.computeBoundingBox();
    const size = new THREE.Vector3();
    geom.boundingBox.getSize(size);
    return size.lengthSq() < 1e-12 ? new THREE.Vector3(1, 1, 1) : size;
  }, [gltf]);

  const { lengthAxis, widthAxis, thicknessAxis } = useMemo(
    () => dominantAxesFromSize(modelSize),
    [modelSize]
  );

  const modelCorrectionQ = useMemo(() => {
    const e = new THREE.Euler(
      modelCorrectionEuler?.[0] ?? 0,
      modelCorrectionEuler?.[1] ?? 0,
      modelCorrectionEuler?.[2] ?? 0
    );
    return new THREE.Quaternion().setFromEuler(e);
  }, [modelCorrectionEuler]);

  const { allSegs, lowestSegs, highestSegs, panels, arrows } = useMemo(() => {
    const preferRoofFaces = roofFaces.length >= 2 && (activeConstruction === 2 || activeConstruction === 6);
    const canUseRoofFaces = roofFaces.length >= 2;

    if (preferRoofFaces || canUseRoofFaces) {

      const combined = {
        allSegs: [],
        lowestSegs: [],
        highestSegs: [],
        panels: [],
        arrows: [],
      };

      let panelOffset = 0;

      for (let i = 0; i < roofFaces.length; i++) {
        const manualPitchForFace = Array.isArray(manualPanelPitchAroundWidthRadByFace)
          ? (Number(
            manualPanelPitchAroundWidthRadByFace[i] ?? manualPanelPitchAroundWidthRad
          ) || 0)
          : manualPanelPitchAroundWidthRad;

        const res = solvePanelsOnSingleRoofFace({
          face: roofFaces[i],
          faceIdx: i,

          modelBounds,
          lengthAxis,
          widthAxis,
          thicknessAxis,
          modelCorrectionQ,

          effectiveSurfaceLift,
          pitchDeltaRad,

          reinforcementEvery,
          panelWidthMultiplier,
          maxPanels,
          minPanels,

          debug,

          forceGableAxis:
            activeConstruction === 2 || activeConstruction === 6
              ? forceGableAxis
              : null,

          manualPanelRot,
          manualPanelPitchAroundWidthRad: manualPitchForFace,
        });

        const facePanels = res.panels.map((p, idx) => ({
          ...p,
          key: `f${i}-p${panelOffset + idx}`,
        }));
        panelOffset += res.panels.length;

        combined.allSegs.push(...res.allSegs);
        combined.lowestSegs.push(...res.lowestSegs);
        combined.highestSegs.push(...res.highestSegs);
        combined.panels.push(...facePanels);
        combined.arrows.push(...res.arrows);
      }

      return combined;
    }

    return solvePanelsFromEdgesLegacy({
      edges,
      modelBounds,
      lengthAxis,
      widthAxis,
      thicknessAxis,
      modelCorrectionQ,
      effectiveSurfaceLift,
      pitchDeltaRad,
      reinforcementEvery,
      panelWidthMultiplier,
      maxPanels,
      minPanels,
      flatSideEps,
      lowestEps,
      debug,
      manualPanelRot,
      manualPanelPitchAroundWidthRad,
    });
  }, [
    edges,
    roofFaces,
    activeConstruction,

    modelSize,
    modelBounds,
    lengthAxis,
    widthAxis,
    thicknessAxis,
    modelCorrectionQ,

    effectiveSurfaceLift,
    pitchDeltaRad,

    reinforcementEvery,
    panelWidthMultiplier,
    maxPanels,
    minPanels,
    flatSideEps,
    lowestEps,

    forceGableAxis,
    manualPanelRot,
    manualPanelPitchAroundWidthRad,
    manualPanelPitchAroundWidthRadByFace,

    pivotAtStart,
    debug,
  ]);

  if (!edges.length && !roofFacesInput.length) {
    return (
      <group position={[0, 1, 0]} scale={[10, 10, 10]} frrustumCulled={false}>
        <Rombek colors={colors}/>
      </group>
    );
  }

  return (
    <group>
      {debug &&
        allSegs.map((e, i) => (
          <React.Fragment key={`edge-${i}`}>
            <DebugEdge a={e.a} b={e.b} />
            <DebugPoint position={e.a} />
            <DebugPoint position={e.b} />
          </React.Fragment>
        ))}

      {debug &&
        lowestSegs.map((e, i) => (
          <React.Fragment key={`lowest-${i}`}>
            <DebugEdge a={e.a} b={e.b} />
            <DebugPoint position={e.a.clone().add(new THREE.Vector3(0, 0.03, 0))} size={0.09} />
            <DebugPoint position={e.b.clone().add(new THREE.Vector3(0, 0.03, 0))} size={0.09} />
          </React.Fragment>
        ))}

      {debug &&
        highestSegs.map((e, i) => (
          <React.Fragment key={`highest-${i}`}>
            <DebugEdge a={e.a} b={e.b} />
            <DebugPoint position={e.a.clone().add(new THREE.Vector3(0, 0.06, 0))} size={0.09} />
            <DebugPoint position={e.b.clone().add(new THREE.Vector3(0, 0.06, 0))} size={0.09} />
          </React.Fragment>
        ))}

      {debug && arrows.map((a) => <DebugArrow key={a.key} from={a.from} to={a.to} />)}

      {panels.map((p) => {
        const Comp = p.reinforced ? RombekWzmocnienie : Rombek
        return (
          <group
            key={p.key}
            position={p.position}
            quaternion={p.quaternion}
            scale={p.scale}
            frustumCulled={false}
          >
            <Comp colors={colors} />
          </group>
        );
      })}
    </group>
  );
}

useGLTF.preload("/models/rombek/rombek.glb");
